//@ts-check
import { Box, Paper, Snackbar } from '@material-ui/core';
import { addHours, getTime, parseISO } from 'date-fns';
import { Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import ReactConfirmAlert from 'react-confirm-alert';
import { connect } from 'react-redux';

import DangerButton from '../../components/button/Danger';
import DefaultButton from '../../components/button/Default';
import PrimaryButton from '../../components/button/Primary';
import SecondaryButton from '../../components/button/Secondary';
import {
    addLaunchProduct,
    addLaunchProductAsset,
    deleteLaunchProduct,
    getLaunchProduct,
    updateLaunchProduct,
} from '../../services/product.service';
import { getAllNestedValues } from '../../utils';
import Alert, { useAlert } from './components/Alert';
import NavigationChips from './components/NavigationChips';
import DetailsView from './form/DetailsView';
import ImagesView from './form/ImagesView';
import OptionsView from './form/OptionsView';
import VideosView from './form/VideosView';
import LaunchesHeader from './LaunchHeader';
import { emptyLaunchProduct, LaunchProductSchema } from './LaunchProduct.schema';

const SubViews = Object.freeze({
    Details: 'Details',
    Images: 'Images',
    Video: 'Video',
    Options: 'Options',
    // 'Info Blocks': 'Info Blocks',
});

const defaultState = {
    loading: true,
    confirmDelete: false,
    product: undefined,
    viewMode: 'ADD', // Whether user is adding or editing a product
    subView: SubViews.Details,
};

const IS_DEV = process.env.NODE_ENV === 'development';

const launchProductReducer = (state = defaultState, { type, payload }) => {
    switch (type) {
        case 'LOADED':
            return { ...state, loading: false };
        case 'FETCH_LAUNCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_LAUNCH_SUCCESS':
            return { ...state, product: payload, loading: false, viewMode: 'EDIT' };
        case 'FETCH_LAUNCH_FAILURE':
            return { ...state, product: undefined, loading: false };
        case 'SELECT_SUBVIEW':
            return { ...state, subView: payload };
        case 'TOGGLE_DELETE_CONFIRMATION':
            return { ...state, confirmDelete: !state.confirmDelete };
        default:
            return state;
    }
};

// This state is only consumed by the Options page, but short of using some sort of
// crude global object, I could see no way of beating the Formik initialValues prop
// other than declaring state here in the parent component, and then passing it through

// Note: this whole problem would probably go away if we refactored this to not switch,
// but to conditionally make the pages visible or not (so they don't get unmounted and
// forget everything). Would need to be tested though
const defaultOptionsState = {
    eanCode: '',
    value: '',
    SKU: '',
    stockLevel: 0,
};

const optionsReducer = (state = defaultOptionsState, { type, payload }) => {
    switch (type) {
        case 'EDIT_EAN':
            return { ...state, eanCode: payload };
        case 'EDIT_SKU':
            return { ...state, SKU: payload };
        case 'EDIT_VALUE':
            return { ...state, value: payload };
        case 'EDIT_STOCK_LEVEL':
            return { ...state, stockLevel: payload };
        case 'RESET':
            return defaultOptionsState;
        default:
            return state;
    }
};

const LaunchProduct = ({ match, history, currentUser }) => {
    const { params } = match;
    const { id: productID } = params;

    const [state, dispatch] = useReducer(launchProductReducer, defaultState);
    const [optionsState, optionsDispatch] = useReducer(optionsReducer, defaultOptionsState);
    const { viewMode } = state;

    const alert = useAlert();

    const toggleDeleteConfirmation = () => dispatch({ type: 'TOGGLE_DELETE_CONFIRMATION' });
    const onSelectSubView = (view) => {
        dispatch({ type: 'SELECT_SUBVIEW', payload: view });
    };

    /* Fetch the product if we're editing an existing product */
    useEffect(() => {
        if (productID) {
            dispatch({ type: 'FETCH_LAUNCH_REQUEST' });
            getLaunchProduct(productID)
                .then((data) => dispatch({ type: 'FETCH_LAUNCH_SUCCESS', payload: data }))
                .catch(() => dispatch({ type: 'FETCH_LAUNCH_FAILURE' }));
        } else {
            dispatch({ type: 'LOADED' });
        }
    }, [productID]);

    const getPageActions = useCallback(
        ({ isSubmitting, errors, dirty, ...form }) => {
            const hasErrors = !!errors ? Object.values(errors).length > 0 : false;

            const SaveButtonType = isSubmitting || hasErrors || !dirty ? SecondaryButton : PrimaryButton;
            const SaveButton = (
                <SaveButtonType
                    key="save-changes"
                    // Only make the button a submit button if there's no errors
                    type={hasErrors ? 'button' : 'submit'}
                    onClick={(e) => {
                        if (hasErrors) {
                            e.preventDefault();
                            const errorMessages = getAllNestedValues(errors);
                            alert.open(...errorMessages);
                        }
                    }}
                    disabled={isSubmitting}
                >
                    Save Changes
                </SaveButtonType>
            );

            const DeleteButton = (
                <DangerButton key="delete-product" onClick={toggleDeleteConfirmation}>
                    Delete Product
                </DangerButton>
            );

            const actions = [SaveButton];

            if (viewMode === 'ADD') return actions;
            if (viewMode === 'EDIT' && currentUser.permissions.includes('launches--delete')) {
                actions.unshift(DeleteButton);
            }

            return actions;
        },
        [viewMode, IS_DEV, toggleDeleteConfirmation],
    );

    const initialFormState = useMemo(() => {
        const formState = { ...emptyLaunchProduct, ...state.product };
        const stringURLToObject = (image) => ({ url: image });
        const existingImage = formState.media.mainImage;
        const mainImage = existingImage
            ? typeof existingImage === 'string'
                ? { url: existingImage }
                : existingImage
            : { url: '' };
        return {
            ...formState,
            media: {
                mainImage: mainImage,
                images: formState.media.images.filter(Boolean).map(stringURLToObject),
                videos: formState.media.videos.filter(Boolean).map(stringURLToObject),
            },
        };
    }, [emptyLaunchProduct, state.product]);

    const onSubmit = async (values, actions) => {
        const form = JSON.parse(JSON.stringify(values));

        // If field validation fails, it appears that numeric fields are being
        // transformed into strings. Not sure if this is us, or whether it is
        // somewhere in the deep dark bowels of formik, but the following are hacks
        // to fix this situation...
        if (typeof form.price.amount !== 'number') {
            form.price.amount = parseInt(form.price.amount);
        }

        if (typeof form.launchDate !== 'number') {
            form.launchDate = getTime(parseISO(form.launchDate));
        }

        for (let option of Object.keys(form.productOptions)) {
            const productOptions = form.productOptions[option];
            for (let idx = 0; idx < productOptions.length; idx++) {
                const stockLevel = productOptions[idx].stockLevel;
                if (typeof stockLevel !== 'number') {
                    productOptions[idx].stockLevel = parseInt(stockLevel);
                }
            }
        }

        form.endDate = getTime(addHours(form.launchDate, 48));

        if (!utils.fileHasBeenUploaded(form.media.mainImage)) {
            const { data, contentType } = await utils.getDataFromDataURL(form.media.mainImage);
            const uploadedURL = await addLaunchProductAsset({
                data,
                contentType,
                path: 'mainImages',
            });
            form.media.mainImage = uploadedURL;
        }

        if (form.media.mainImage && form.media.mainImage.url) {
            form.media.mainImage = form.media.mainImage.url; // Convert url object to string
        }

        /**
         * Create upload requests, but don't wait for them.
         * Allows images and videos to be uploaded in parallel
         */
        const imageUploads = Promise.all(
            form.media.images.map(async (i) => {
                if (utils.fileHasBeenUploaded(i)) return i.url;
                const { data, contentType } = await utils.getDataFromDataURL(i);
                const uploadedURL = await addLaunchProductAsset({
                    data,
                    contentType,
                    path: 'images',
                });
                return uploadedURL;
            }),
        );

        /**
         * Create upload requests, but don't wait for them.
         * Allows images and videos to be uploaded in parallel
         */
        const videoUploads = Promise.all(
            form.media.videos.map(async (v) => {
                if (utils.fileHasBeenUploaded(v)) return v.url;
                const { data, contentType } = await utils.getDataFromDataURL(v);
                const uploadedURL = await addLaunchProductAsset({
                    data,
                    contentType,
                    path: 'videos',
                });
                return uploadedURL;
            }),
        );

        const [images, videos] = await Promise.all([imageUploads, videoUploads]);

        form.media = {
            ...form.media,
            images,
            videos,
        };

        try {
            await saveChanges(form);
            history.push('/launches');
        } catch (e) {
            actions.setSubmitting(false);
            alert.open('Failed to save launch product');
        }
    };

    const saveChanges = async (form) => {
        if (viewMode === 'ADD') {
            await addLaunchProduct(form);
        } else {
            await updateLaunchProduct(productID, form);
        }
    };

    const onDelete = async (form) => {
        try {
            await deleteLaunchProduct(productID);
            history.push('/launches');
        } catch (e) {
            alert.open('Failed to delete launch product');
            form.setSubmitting(false);
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialFormState}
            validationSchema={LaunchProductSchema}
            validateOnMount={true}
            onSubmit={onSubmit}
        >
            {(form) => {
                return (
                    <form
                        onSubmit={form.handleSubmit}
                        // style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}
                    >
                        <Box display="flex" flexDirection="column" justifyContent="spaceBetween" height="100%">
                            <LaunchesHeader
                                leftItems={[
                                    <DefaultButton onClick={history.goBack} key="back">
                                        Go Back
                                    </DefaultButton>,
                                ]}
                                rightItems={getPageActions(form)}
                            />
                            {state.confirmDelete && (
                                <ReactConfirmAlert
                                    customUI={() => {
                                        return (
                                            <Box component={Paper} style={{ padding: 32 }}>
                                                <h1>Delete Product?</h1>
                                                <p>Are you sure you want to permanently delete this product?</p>
                                                <DefaultButton onClick={toggleDeleteConfirmation}>Cancel</DefaultButton>
                                                <DangerButton onClick={() => onDelete(form)}>Delete</DangerButton>
                                            </Box>
                                        );
                                    }}
                                />
                            )}
                            <Snackbar
                                open={alert.isOpen}
                                autoHideDuration={10000}
                                onClose={alert.onClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            >
                                <Alert onClose={alert.onClose} severity="error">
                                    {alert.message.map((message) => {
                                        return <div key={message}>{message}</div>;
                                    })}
                                </Alert>
                            </Snackbar>
                            <Box flex={1} mx={6} my={1} display="flex" flexDirection="column" justifyContent="baseline">
                                <h2>{viewMode === 'ADD' ? 'Add Launch Product' : state.product.name}</h2>
                                <NavigationChips
                                    items={Object.values(SubViews)}
                                    selected={state.subView}
                                    onSelect={onSelectSubView}
                                />
                                <Box>
                                    <PageContent
                                        subView={state.subView}
                                        optionsState={optionsState}
                                        optionsDispatch={optionsDispatch}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </form>
                );
            }}
        </Formik>
    );
};

function mapStateToProps(state) {
    return {
        currentUser: state.auth.currentUser,
    };
}

export default connect(mapStateToProps)(LaunchProduct);

const PageContent = ({ subView, optionsState, optionsDispatch }) => {
    switch (subView) {
        case SubViews.Details:
            return <DetailsView />;
        case SubViews.Options:
            return <OptionsView state={optionsState} dispatch={optionsDispatch} />;
        case SubViews.Images:
            return <ImagesView />;
        case SubViews.Video:
            return <VideosView />;
        case SubViews['Info Blocks']:
            return <>{subView}</>;
        default:
            return null;
    }
};

const utils = {
    fileHasBeenUploaded: ({ url, name }) => {
        if (name) return false;
        return url.match(/^http[s]?/); // Non-uploaded files will have data:image here instead
    },
    getDataFromDataURL: async ({ url }) => {
        const response = await fetch(url);
        const blob = await response.blob();
        const contentType = blob.type;

        // Extract the "content" part of the URL, stripping off the `data:image/jpeg;base64,`
        const matches = url.match(/data:.*;base64,(.*)/);
        const data = matches[1];

        return { data, contentType };
    },
};
